/**
 * 払った瞬間の演出 (2026-09-19 デモ。Codex (gpt-6-astra) の 6 つの習作を土台に作り直した)。
 *
 * オーナー指示: 時刻の横の赤い「−7 守」は見る気が失せる。引かれたと思わずに、
 * **払ったことでこの守経済に貢献した**と思えるように。最先端の技術を感じて感動する演出で。
 * ただし**スクロールのたびに毎回出ても不自然でない**こと。
 *
 * 設計の芯 (どの案も同じ):
 *   1. **終わった姿はどの投稿にもある灰色の ✓**。演出は「✓ が生まれる瞬間」なので、
 *      何十回見ても新しい物体が増えていく感じにならない。
 *   2. **守は消えずに書いた人へ届く**。「−」記号と赤は使わない。額は「7 守」だけ。
 *   3. **行もカードも 1px も動かさない**。重ね描き (.pv-fx、absolute) だけで描く。
 *      旧実装は ✓ の後ろに inline で差し込んでいたので、その 3 秒間 @ハンドルが縮んでいた。
 *   4. 何枚も同時に解錠されたら**画面の上から順に少しずつずらす** (STAGGER_MS)。
 *   5. 出どころ (自分 / 町 / 書いた人) で演出を変えない。変わるのは文言だけ。
 *
 * ⚠ 守っていること (paidview.ts と同じ):
 *   - rAF も Web Animations API も使わない。CSS の @keyframes + クラス + setTimeout だけ。
 *   - opacity プロパティを使わない (ライトの全称ルールに潰される)。filter: opacity() で。
 *   - 根の .pv-fx には **インラインで visibility:hidden** を置き、CSS 側の
 *     `visibility: visible !important` で開ける。端末が古い v2.min.css を握っていると
 *     中の要素が素の span として本文の下に文字列で出てしまうので、その保険。
 *   - `.post` は content-visibility:auto (= paint containment) なので、演出は必ず
 *     カードの中に収める (はみ出た分は切られる)。
 */

export type FxKind = "self" | "town" | "author";

/** paidview.ts の Receipt のうち、演出に要る 2 つ。 */
export interface FxReceipt {
  stamp: string;
  kind: FxKind;
}

/** 殻の識別線の位置 (差し替える直前に測る。カード基準の px)。 */
export interface ShellRect {
  x: number;
  y: number;
  h: number;
  /** 箱の幅 (ホログラムの縁をほどく演出が使う。無ければ本文列の幅)。 */
  w?: number;
}

export const VARIANTS = [
  "unified", "transmit", "mint", "sign", "develop", "circuit", "legacy",
  // ホログラムの枠に決めたあとの「ひと続きのセット」(2026-09-19 オーナー「全部 (ホログラム前提で)」)
  "hcircuit", "hfoil", "hprism", "hfringe",
  // Codex (gpt-6-astra) のホログラムのセットから、Claude の 4 つと重ならない 2 つ
  "hdiffract", "htransfer",
] as const;
export type Variant = (typeof VARIANTS)[number];

/** デモ中の選択 (/testpage/paid-fx の切り替えが書く)。決まったら定数 1 つにする。 */
// 2026-09-19 に「干渉縞」に決まったので、比較で選んだ古い値を読まないようキーを改めた。
const LS_KEY = "mp_pv_fx_2";
const DEFAULT_VARIANT: Variant = "hfringe"; // 2026-09-19 オーナー決定「干渉縞に決めました」

/** 同時に解錠されたカードのずらし幅と、ずらしの上限枚数 (それ以降は同じ遅れ)。 */
export const STAGGER_MS = 120;
const STAGGER_CAP = 7;

/**
 * 案ごとの時間割 (ms)。land = 本物の ✓ を出す時刻 / label = 文言が出はじめる時刻 /
 * hit = アバターが受け取る時刻 (送達・回路だけ)。CSS の @keyframes と揃えること。
 * 重ね描きを外すのは「文言が消え終わる時刻 + 60ms」(LABEL_MS)。先に外すと文言が途中で切れる
 * (Codex レビュー: 回路は文言が見えたまま親ごと消えていた)。
 */
const TIMING: Record<
  Exclude<Variant, "legacy">,
  { land: number; label: number; hit?: number; labelMs?: number; timeSlot?: boolean }
> = {
  transmit: { land: 780, label: 90, hit: 680 },
  mint: { land: 820, label: 300 },
  sign: { land: 700, label: 320 },
  develop: { land: 700, label: 80 },
  circuit: { land: 760, label: 540, hit: 540 },
  // 統合 (2026-09-19 オーナー決定「全部いいので、全部のいい箇所を統合。回路の方向で」)。
  // 時間割は Codex レビュー (2 回目) で「一度に見る場所は 1 つ」に並べ直した:
  //   0〜340 上へ走る電流と本文 / 340〜540 角を曲がってアバターへ / 540〜900 受け取る (輪・弾み・硬貨) /
  //   800〜1000 ヘッダーの下を ✓ へ戻って上がる / 1000〜1312 ペン先が ✓ を書く / 1310〜 額 (1.4 秒)。
  unified: { land: 1320, label: 1310, hit: 540, labelMs: 1400 },
  // ホログラムのセット。虹の回路は「縁がほどける」180ms ぶん統合より後ろへずれる。
  hcircuit: { land: 1500, label: 1490, hit: 720, labelMs: 1400 },
  hfoil: { land: 640, label: 180, hit: 700, labelMs: 1600 },
  hprism: { land: 600, label: 120, hit: 420, labelMs: 1600 },
  // 干渉縞 (2026-09-19 オーナー決定)。額は ✓ の右や上に浮かせず、**時刻の場所で入れ替える**
  // (名前が長いと額が時刻に重なっていた、オーナー指摘)。
  hfringe: { land: 560, label: 100, hit: 600, labelMs: 1600, timeSlot: true },
  hdiffract: { land: 1000, label: 900, hit: 440, labelMs: 1500 },
  htransfer: { land: 900, label: 760, labelMs: 1600 },
};
/** 文言の出てから消えるまで (CSS の .pv-fx-label の animation-duration)。 */
const LABEL_MS = 1800;
/** 現像の走査の長さ (CSS の pv-d-scan / pv-dev-mask と揃える)。 */
const DEVELOP_MS = 560;

export function currentVariant(): Variant {
  try {
    const v = window.localStorage.getItem(LS_KEY);
    if (v && (VARIANTS as readonly string[]).includes(v)) return v as Variant;
  } catch {
    /* プライベートモード等 */
  }
  return DEFAULT_VARIANT;
}

export function setVariant(v: Variant): void {
  try {
    window.localStorage.setItem(LS_KEY, v);
  } catch {
    /* 保存できなくても今回の再生には効かせる (呼び出し側が variant を渡す) */
  }
}

/** 再生中の「動きを減らす」設定 (play() の頭で読む)。 */
let reduced = false;

/**
 * カードごとの再生の状態。同じカードで再生し直したとき、前のタイマーが新しい演出を
 * 途中で片付けないように、タイマーと付けたものを全部ここに持って cancel() で一度に外す。
 */
interface Run {
  timers: number[];
  root: HTMLElement | null;
  mark: HTMLElement | null;
  avatar: HTMLElement | null;
  main: HTMLElement | null;
  /** 額と入れ替えている時刻 (「時刻の場所で入れ替える」案だけ)。 */
  time: HTMLElement | null;
}
const runs = new WeakMap<HTMLElement, Run>();

function later(run: Run, fn: () => void, ms: number): void {
  run.timers.push(window.setTimeout(fn, ms));
}

/** カードの演出を途中でも完成の状態 (灰色の ✓ だけ) にして片付ける。何度呼んでもよい。 */
export function cancel(card: HTMLElement): void {
  const run = runs.get(card);
  if (!run) return;
  runs.delete(card);
  for (const id of run.timers) window.clearTimeout(id);
  run.root?.remove();
  run.mark?.classList.remove("pv-fx-hold", "pv-fx-land");
  run.avatar?.classList.remove("pv-fx-recv");
  if (run.time) {
    run.time.classList.remove("pv-fx-timeflip");
    run.time.style.removeProperty("--pv-tf-d");
    run.time.style.removeProperty("--pv-tf-dur");
  }
  card.classList.remove("pv-dev", "pv-rev");
  if (run.main) {
    for (const p of ["--pv-hh", "--pv-tall", "--pv-from", "--pv-to", "--pv-dd"]) run.main.style.removeProperty(p);
  }
}

function reducedMotion(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// 位置
// ---------------------------------------------------------------------------

interface Geo {
  /** ✓ (詳細ページは時刻の文字の右端) の中心と大きさ。 */
  mx: number;
  my: number;
  mw: number;
  mh: number;
  /** ✓ の右に文言を置ける幅 (ケバブの手前まで)。 */
  room: number;
  /** アバターの中心と半径 (無ければ null)。 */
  ax: number;
  ay: number;
  ar: number;
  hasAvatar: boolean;
  /** 本文列 (.post-main-col) の位置と、ヘッダーの高さ。 */
  bx: number;
  by: number;
  bw: number;
  bh: number;
  hh: number;
  hasMain: boolean;
  /** 本物の ✓ (無ければ null = 詳細ページ)。 */
  mark: HTMLElement | null;
  /** ✓ の文字の大きさ (重ね描きの ✓ を同じ大きさで描く)。 */
  font: string;
  /** ヘッダーの時刻 (額を「時刻の場所で入れ替える」ときに使う。詳細ページは null)。 */
  time: { l: number; r: number; cy: number; h: number; el: HTMLElement } | null;
  /** 名前・時刻の行の左端と、カードの幅。 */
  metaL: number;
  cw: number;
}

function measure(card: HTMLElement): Geo | null {
  const base = card.getBoundingClientRect();
  if (base.width === 0) return null;
  const mark = card.querySelector<HTMLElement>(".posthead .post-paid-mark");
  let m: DOMRect | null = null;
  let font = "";
  if (mark) {
    m = mark.getBoundingClientRect();
    const cs = window.getComputedStyle(mark);
    font = `${cs.fontWeight} ${cs.fontSize}/${cs.lineHeight} ${cs.fontFamily}`;
  } else {
    // 投稿詳細: ✓ が無いので、本文下の時刻の文字の右端に置く (DOM は触らない)。
    const dt = card.querySelector<HTMLElement>(".post-detail-time");
    if (!dt) return null;
    const range = document.createRange();
    range.selectNodeContents(dt);
    const rects = range.getClientRects();
    const last = rects.length ? rects[rects.length - 1] : dt.getBoundingClientRect();
    m = new DOMRect(last.right + 4, last.top, 12, last.height);
  }
  const mx = m.left - base.left + m.width / 2;
  const my = m.top - base.top + m.height / 2;

  // 文言を置ける幅: ✓ の右端から、ケバブ (無ければカードの右の余白) まで。
  const kebab = card.querySelector<HTMLElement>(".posthead .post-meta-right");
  const limit = kebab ? kebab.getBoundingClientRect().left : base.right - 12;
  const room = Math.max(0, limit - m.right - 6);

  const av = card.querySelector<HTMLElement>(".post-avatar-col .avatar, .post-detail-author .avatar, .avatar");
  let ax = 0;
  let ay = 0;
  let ar = 0;
  if (av) {
    const r = av.getBoundingClientRect();
    ax = r.left - base.left + r.width / 2;
    ay = r.top - base.top + r.height / 2;
    ar = r.width / 2;
  }

  let time: Geo["time"] = null;
  const tEl = mark ? card.querySelector<HTMLElement>(".posthead .time-toggle") : null;
  if (tEl) {
    const r = tEl.getBoundingClientRect();
    if (r.width > 0) time = { l: r.left - base.left, r: r.right - base.left, cy: r.top - base.top + r.height / 2, h: r.height, el: tEl };
  }
  const metaEl = card.querySelector<HTMLElement>(".posthead .post-author-meta");
  const metaL = metaEl ? metaEl.getBoundingClientRect().left - base.left : 0;

  const main = card.querySelector<HTMLElement>(".post-main-col");
  const head = card.querySelector<HTMLElement>(".posthead");
  let bx = 0;
  let by = 0;
  let bw = 0;
  let bh = 0;
  let hh = 0;
  if (main) {
    const r = main.getBoundingClientRect();
    bx = r.left - base.left;
    by = r.top - base.top;
    bw = r.width;
    bh = r.height;
    hh = head ? head.getBoundingClientRect().bottom - r.top : 0;
  }
  return {
    mx, my, mw: m.width, mh: m.height, room,
    ax, ay, ar, hasAvatar: !!av && ar > 0,
    bx, by, bw, bh, hh, hasMain: !!main && bh > 0,
    mark, font, time, metaL, cw: base.width,
  };
}

// ---------------------------------------------------------------------------
// 部品
// ---------------------------------------------------------------------------

function span(cls: string, text?: string): HTMLSpanElement {
  const s = document.createElement("span");
  s.className = cls;
  if (text !== undefined) s.textContent = text;
  return s;
}

/**
 * 文言。額 (「7 守」) は数字だけ一段大きく。町 / 書いた人は文のまま。
 * 置き場所は ✓ の右 (入るとき) か ✓ の上 (名前が長くて右に入らないとき)。
 */
function label(rc: FxReceipt, g: Geo, timeSlot = false): HTMLSpanElement {
  const el = span("pv-fx-label");
  const m = rc.kind === "self" ? /^[^0-9]*([0-9]+)\s*守/.exec(rc.stamp) : null;
  if (m) {
    const b = document.createElement("b");
    b.textContent = m[1];
    el.appendChild(b);
    el.appendChild(document.createTextNode(" 守"));
  } else {
    el.textContent = rc.stamp;
  }
  // 幅は描く前に分からないので、文字数からの見積もり (12px の全角 ≒ 12px、数字 ≒ 7px)。
  const est = [...el.textContent!].reduce((n, ch) => n + (/[\x20-\x7e]/.test(ch) ? 7 : 12), 0) + 2;
  el.dataset.w = String(est);
  if (timeSlot && g.time) {
    // 時刻の場所で入れ替える: 額の右端を時刻の右端にそろえる (✓ のすぐ左に来る)。
    el.dataset.lp = "time";
    el.style.right = `${Math.round(g.cw - g.time.r)}px`;
    el.style.top = `${Math.round(g.time.cy)}px`;
  } else if (g.room >= est) {
    el.dataset.lp = "right";
    el.style.left = `${Math.round(g.mx + g.mw / 2 + 5)}px`;
    el.style.top = `${Math.round(g.my)}px`;
  } else {
    el.dataset.lp = "up";
    el.style.left = `${Math.round(g.mx)}px`;
    el.style.top = `${Math.round(g.my - g.mh / 2 - 9)}px`;
  }
  return el;
}

/** 本物の ✓ と同じ位置・同じ字形の重ね描き (光る ✓)。 */
function glowMark(g: Geo): HTMLSpanElement {
  const el = span("pv-fx-mark", "✓");
  el.style.left = `${Math.round(g.mx - g.mw / 2)}px`;
  el.style.top = `${Math.round(g.my - g.mh / 2)}px`;
  el.style.width = `${Math.ceil(g.mw)}px`;
  el.style.height = `${Math.ceil(g.mh)}px`;
  if (g.font) el.style.font = g.font;
  return el;
}

/** アバターが受け取る輪 (送達と回路)。 */
function halo(g: Geo): HTMLSpanElement {
  const el = span("pv-fx-halo");
  const d = Math.round(g.ar * 2 + 10);
  el.style.left = `${Math.round(g.ax - d / 2)}px`;
  el.style.top = `${Math.round(g.ay - d / 2)}px`;
  el.style.width = `${d}px`;
  el.style.height = `${d}px`;
  el.appendChild(span("pv-fx-halo-ring"));
  el.appendChild(span("pv-fx-halo-flash"));
  return el;
}

// ---------------------------------------------------------------------------
// 案ごとの組み立て
// ---------------------------------------------------------------------------

/** ① 送達: ✓ から光の粒が書いた人のアバターへ飛び、アバターが受け取る。 */
function buildTransmit(root: HTMLElement, g: Geo): void {
  root.appendChild(glowMark(g));
  if (g.hasAvatar) {
    const dx = g.ax - g.mx;
    const dy = g.ay - g.my;
    const path = span("pv-t-path");
    path.style.left = `${Math.round(g.mx)}px`;
    path.style.top = `${Math.round(g.my)}px`;
    path.style.setProperty("--dx", `${Math.round(dx)}px`);
    path.style.setProperty("--dy", `${Math.round(dy)}px`);
    const arc = span("pv-t-arc");
    const comet = span("pv-t-comet");
    comet.style.setProperty("--ang", `${Math.round((Math.atan2(dy, dx) * 180) / Math.PI)}deg`);
    comet.appendChild(span("pv-t-orb"));
    arc.appendChild(comet);
    path.appendChild(arc);
    root.appendChild(path);
    root.appendChild(halo(g));
  }
  const burst = span("pv-t-burst");
  burst.style.left = `${Math.round(g.mx)}px`;
  burst.style.top = `${Math.round(g.my)}px`;
  root.appendChild(burst);
}

/** ② 刻印: ホログラムの「守」の硬貨が回って ✓ に裏返る。 */
function buildMint(root: HTMLElement, g: Geo): void {
  const coin = span("pv-m-coin");
  // 硬貨は ✓ より大きいので、左の時刻の文字に掛からないよう少し右へ寄せる。
  coin.style.left = `${Math.round(g.mx + 2)}px`;
  coin.style.top = `${Math.round(g.my)}px`;
  const face = span("pv-m-face");
  face.appendChild(span("pv-m-glyph", "守"));
  face.appendChild(span("pv-m-sheen"));
  coin.appendChild(face);
  root.appendChild(coin);
  const spark = span("pv-m-spark");
  spark.style.left = `${Math.round(g.mx + 7)}px`;
  spark.style.top = `${Math.round(g.my - 7)}px`;
  root.appendChild(spark);
  const mk = glowMark(g);
  mk.classList.add("pv-m-flip");
  root.appendChild(mk);
}

/** ③ 署名: 光るペン先が ✓ を書き、そのまま額を書き添える。 */
function buildSign(root: HTMLElement, g: Geo): void {
  const bloom = span("pv-s-bloom");
  bloom.style.left = `${Math.round(g.mx)}px`;
  bloom.style.top = `${Math.round(g.my)}px`;
  root.appendChild(bloom);
  const mk = glowMark(g);
  mk.classList.add("pv-s-draw");
  root.appendChild(mk);
  const pen = span("pv-s-pen");
  pen.style.left = `${Math.round(g.mx)}px`;
  pen.style.top = `${Math.round(g.my)}px`;
  pen.style.setProperty("--w", `${Math.round(g.mw)}px`);
  pen.style.setProperty("--h", `${Math.round(g.mh)}px`);
  root.appendChild(pen);
}

/**
 * ④ 現像: 光の走査線が本文列を上から下へ通り、線の通ったところから本文が浮かび上がる。
 * 本文列 (.post-main-col) にマスクを掛けるので、ヘッダー (殻のときから見えていた行) は
 * 常に見えたまま (マスクの 1 枚目がヘッダーの高さだけ塗ってある)。
 */
function buildDevelop(root: HTMLElement, g: Geo, card: HTMLElement, delay: number, run: Run): void {
  const mk = glowMark(g);
  mk.classList.add("pv-d-mark");
  root.appendChild(mk);
  if (!g.hasMain) return;
  const reveal = Math.max(0, g.bh - g.hh);
  const scan = span("pv-d-scan");
  scan.style.left = `${Math.round(g.bx - 6)}px`;
  scan.style.width = `${Math.round(g.bw + 12)}px`;
  scan.style.top = `${Math.round(g.by + g.hh)}px`;
  scan.style.setProperty("--run", `${Math.round(reveal + 18)}px`);
  scan.appendChild(span("pv-d-wash"));
  root.appendChild(scan);
  if (reduced) return;
  const main = card.querySelector<HTMLElement>(".post-main-col");
  if (!main) return;
  // マスクの 2 枚目 (グラデ) は高さ tall で「不透明 → 最後の 36px でぼかし」。ぼかしの中央を
  // ヘッダーの下端 → 本文列の下端 +18px へ動かす (走査線と同じ速さ・同じ曲線)。
  // tall を本文列より 600px 高くしておくのは、画像の読み込みで本文列が伸びても
  // 上側 (= 見えている側) が欠けないようにするため。
  const tall = Math.round(g.bh + 600);
  main.style.setProperty("--pv-hh", `${Math.round(g.hh)}px`);
  main.style.setProperty("--pv-tall", `${tall}px`);
  main.style.setProperty("--pv-from", `${Math.round(g.hh + 18 - tall)}px`);
  main.style.setProperty("--pv-to", `${Math.round(g.bh + 36 - tall)}px`);
  main.style.setProperty("--pv-dd", `${delay}ms`);
  run.main = main;
  card.classList.add("pv-dev");
  // マスクは走査が終わったらすぐ外す (残すほど本文の再描画が重くなる)。
  later(run, () => {
    card.classList.remove("pv-dev");
    for (const p of ["--pv-hh", "--pv-tall", "--pv-from", "--pv-to", "--pv-dd"]) main.style.removeProperty(p);
    run.main = null;
  }, delay + DEVELOP_MS + 40);
}

/**
 * ⑤ 回路: 殻の左端にあった識別線が電流になって上へ走り、角を曲がってアバターへ入る。
 * 殻の線と同じ位置・同じ色から始めるので、差し替えの瞬間がつながって見える。
 */
function buildCircuit(root: HTMLElement, g: Geo, shell: ShellRect | null): void {
  let sx: number;
  let sy: number;
  let sh: number;
  if (shell) {
    ({ x: sx, y: sy, h: sh } = shell);
  } else if (g.hasMain) {
    sx = g.bx;
    sy = g.by + g.hh + 2;
    sh = Math.max(24, Math.min(g.bh - g.hh - 30, 120));
  } else {
    buildTransmit(root, g);
    return;
  }
  const line = span("pv-c-line");
  line.style.left = `${Math.round(sx - 1)}px`;
  line.style.top = `${Math.round(sy)}px`;
  line.style.height = `${Math.round(sh)}px`;
  root.appendChild(line);
  const up = span("pv-c-up");
  up.style.left = `${Math.round(sx - 1)}px`;
  up.style.top = `${Math.round(sy)}px`;
  up.style.setProperty("--run", `${Math.round(sh)}px`);
  root.appendChild(up);
  if (g.hasAvatar) {
    // 横の線はアバターの円周で止める (線の高さが円の中なら交点、外なら円の右下あたり)。
    const dy = sy - g.ay;
    const endX = Math.abs(dy) < g.ar ? g.ax + Math.sqrt(g.ar * g.ar - dy * dy) : g.ax + g.ar * 0.72;
    const trace = span("pv-c-trace");
    trace.style.left = `${Math.round(endX)}px`;
    trace.style.top = `${Math.round(sy - 1)}px`;
    trace.style.width = `${Math.max(4, Math.round(sx - endX))}px`;
    root.appendChild(trace);
    const bolt = span("pv-c-bolt");
    bolt.style.left = `${Math.round(sx)}px`;
    bolt.style.top = `${Math.round(sy)}px`;
    bolt.style.setProperty("--run", `${Math.round(endX - sx)}px`);
    root.appendChild(bolt);
    root.appendChild(halo(g));
  }
  const mk = glowMark(g);
  mk.classList.add("pv-c-mark");
  root.appendChild(mk);
}

/**
 * ◎ 統合 (回路を土台に 5 案を 1 つの流れに):
 *   0〜360ms    殻の識別線が電流になって下から上へ走る。通ったところから本文が浮かぶ (回路 + 現像)
 *   340〜520ms  角を曲がり、彗星の尾を引いてアバターの円周へ入る (送達)
 *   520ms〜     アバターが虹の輪で受け取って弾み、「守」の硬貨が書いた人へ吸い込まれる (刻印)
 *   560〜800ms  光がヘッダーの下を通って ✓ の位置へ戻る (回路が閉じる)
 *   760ms〜     ペン先が ✓ を書き、そのまま額を書き添える (署名)
 * どこかの部品の条件が揃わない (アバターが無い・本文列が無い) ときは、その部品だけ飛ばす。
 */
function buildUnified(root: HTMLElement, g: Geo, card: HTMLElement, shell: ShellRect | null, delay: number, run: Run): void {
  let sx: number;
  let sy: number;
  let sh: number;
  if (shell) {
    ({ x: sx, y: sy, h: sh } = shell);
  } else if (g.hasMain) {
    sx = g.bx;
    sy = g.by + g.hh + 2;
    sh = Math.max(24, Math.min(g.bh - g.hh - 30, 120));
  } else {
    sx = g.mx;
    sy = g.my;
    sh = 0;
  }

  // 1. 識別線と、上へ走る電流
  if (sh > 0) {
    const line = span("pv-c-line");
    line.style.left = `${Math.round(sx - 1)}px`;
    line.style.top = `${Math.round(sy)}px`;
    line.style.height = `${Math.round(sh)}px`;
    root.appendChild(line);
    const up = span("pv-c-up");
    up.style.left = `${Math.round(sx - 1)}px`;
    up.style.top = `${Math.round(sy)}px`;
    up.style.setProperty("--run", `${Math.round(sh)}px`);
    root.appendChild(up);
    revealUpward(card, g, sy, sh, delay, run);
  }

  // 2〜3. 角を曲がってアバターへ → 受け取る
  if (g.hasAvatar && sh > 0) {
    const dy = sy - g.ay;
    const endX = Math.abs(dy) < g.ar ? g.ax + Math.sqrt(g.ar * g.ar - dy * dy) : g.ax + g.ar * 0.72;
    const trace = span("pv-c-trace");
    trace.style.left = `${Math.round(endX)}px`;
    trace.style.top = `${Math.round(sy - 1)}px`;
    trace.style.width = `${Math.max(4, Math.round(sx - endX))}px`;
    root.appendChild(trace);
    const bolt = span("pv-c-bolt pv-u-bolt");
    bolt.style.left = `${Math.round(sx)}px`;
    bolt.style.top = `${Math.round(sy)}px`;
    bolt.style.setProperty("--run", `${Math.round(endX - sx)}px`);
    root.appendChild(bolt);
  }
  if (g.hasAvatar) {
    root.appendChild(halo(g));
    const coin = span("pv-u-coin");
    coin.style.left = `${Math.round(g.ax + g.ar * 0.72)}px`;
    coin.style.top = `${Math.round(g.ay + g.ar * 0.72)}px`;
    coin.style.setProperty("--in-x", `${Math.round(-g.ar * 0.72)}px`);
    coin.style.setProperty("--in-y", `${Math.round(-g.ar * 0.72)}px`);
    const face = span("pv-m-face");
    face.appendChild(span("pv-m-glyph", "守"));
    face.appendChild(span("pv-m-sheen"));
    coin.appendChild(face);
    root.appendChild(coin);

    // 4. ヘッダーの下を ✓ へ戻る (✓ がアバターより右にあるときだけ)
    const ry = g.by + g.hh - 1;
    const ddy = ry - g.ay;
    const rx0 = Math.abs(ddy) < g.ar ? g.ax + Math.sqrt(g.ar * g.ar - ddy * ddy) : g.ax + g.ar;
    const rx1 = g.mx - g.mw / 2;
    if (g.hasMain && rx1 - rx0 > 24) {
      const ret = span("pv-u-ret");
      ret.style.left = `${Math.round(rx0)}px`;
      ret.style.top = `${Math.round(ry)}px`;
      ret.style.width = `${Math.round(rx1 - rx0)}px`;
      root.appendChild(ret);
      // 先頭の粒は最後に ✓ の高さまで上がる (回路が閉じる)。
      const head = span("pv-u-rhead");
      head.style.left = `${Math.round(rx0)}px`;
      head.style.top = `${Math.round(ry)}px`;
      head.style.setProperty("--run", `${Math.round(rx1 - rx0 + g.mw * 0.3)}px`);
      head.style.setProperty("--rise", `${Math.round(g.my - ry)}px`);
      root.appendChild(head);
    }
  }

  // 5. ペン先が ✓ を書き、額を書き添える
  const mk = glowMark(g);
  mk.classList.add("pv-s-draw");
  root.appendChild(mk);
  const pen = span("pv-s-pen");
  pen.style.left = `${Math.round(g.mx)}px`;
  pen.style.top = `${Math.round(g.my)}px`;
  pen.style.setProperty("--w", `${Math.round(g.mw)}px`);
  pen.style.setProperty("--h", `${Math.round(g.mh)}px`);
  root.appendChild(pen);
}

/**
 * 本文列を下から上へ浮かび上がらせる (電流が通ったところから)。完全には隠さない —
 * 通る前も 28% の濃さで見えている (読むたびに本文を消すと負担になる、Codex レビュー)。
 * マスクの 1 枚目 = ヘッダー (殻のときから見えていた行) は常に見せる。
 */
function revealUpward(card: HTMLElement, g: Geo, sy: number, sh: number, delay: number, run: Run): void {
  if (reduced || !g.hasMain) return;
  const main = card.querySelector<HTMLElement>(".post-main-col");
  if (!main) return;
  const half = Math.round(g.bh + 200);
  const from = sy + sh - g.by; // 本文列の中での縁の出発点 (殻の下端)
  // 縁は電流と同じ所を同じ速さで上がる (殻の下端 → 殻の上端)。殻の上端より上はヘッダーとの
  // すき間だけなので、最後にぼかしの半分 (16px) だけ余分に上げて本文を全部出しきる。
  const to = sy - g.by - 16;
  main.style.setProperty("--pv-hh", `${Math.round(g.hh)}px`);
  main.style.setProperty("--pv-tall", `${half * 2}px`);
  main.style.setProperty("--pv-from", `${Math.round(from - half)}px`);
  main.style.setProperty("--pv-to", `${Math.round(to - half)}px`);
  main.style.setProperty("--pv-dd", `${delay}ms`);
  run.main = main;
  card.classList.add("pv-rev");
  later(run, () => {
    card.classList.remove("pv-rev");
    for (const p of ["--pv-hh", "--pv-tall", "--pv-from", "--pv-to", "--pv-dd"]) main.style.removeProperty(p);
    run.main = null;
  }, delay + 380);
}

// ---------------------------------------------------------------------------
// ホログラムのセット (2026-09-19)。殻の枠 = ホログラム (虹の細い縁) が前提。
// 額はどれも「ホログラムの箔の文字」(虹のグラデに光が一度走る)。
// ---------------------------------------------------------------------------

/** ◇ 虹の回路: 殻の虹の縁がほどけて左端に集まり、そこから ◎ 統合の流れを虹色で走る。 */
function buildHCircuit(root: HTMLElement, g: Geo, card: HTMLElement, shell: ShellRect | null, delay: number, run: Run): void {
  if (shell) {
    const ring = span("pv-h-unzip");
    ring.style.left = `${Math.round(shell.x)}px`;
    ring.style.top = `${Math.round(shell.y)}px`;
    ring.style.width = `${Math.round(shell.w || g.bw)}px`;
    ring.style.height = `${Math.round(shell.h)}px`;
    root.appendChild(ring);
  }
  // 残りは統合の部品を 180ms 遅らせて使う (--pv-d を差し替えた入れ物に入れる)。
  const sub = span("pv-fx-sub");
  sub.style.setProperty("--pv-d", `${delay + 180}ms`);
  sub.style.setProperty("--pv-hit", `${TIMING.unified.hit}ms`); // 入れ物の中では統合と同じ時刻
  root.appendChild(sub);
  buildUnified(sub, g, card, shell, delay + 180, run);
}

/** ◇ 箔押し: ✓ に虹の箔が押され、光の帯がヘッダーを ✓ から書いた人へ流れる。 */
function buildHFoil(root: HTMLElement, g: Geo): void {
  const disc = span("pv-hf-disc");
  disc.style.left = `${Math.round(g.mx)}px`;
  disc.style.top = `${Math.round(g.my)}px`;
  root.appendChild(disc);
  const mk = glowMark(g);
  mk.classList.add("pv-hf-stamp");
  root.appendChild(mk);
  if (g.hasAvatar) {
    const x0 = Math.max(0, g.ax - g.ar - 6);
    const x1 = g.mx + g.mw / 2 + 8;
    const top = Math.max(0, Math.min(g.ay - g.ar, g.by) - 4);
    const bottom = Math.max(g.ay + g.ar, g.by + g.hh) + 4;
    if (x1 - x0 > 30) {
      const band = span("pv-hf-band");
      band.style.left = `${Math.round(x0)}px`;
      band.style.top = `${Math.round(top)}px`;
      band.style.width = `${Math.round(x1 - x0)}px`;
      band.style.height = `${Math.round(bottom - top)}px`;
      root.appendChild(band);
    }
    root.appendChild(halo(g));
  }
}

/** ◇ 分光: ✓ の白い光が虹に分かれて扇になり、書いた人を照らす。 */
function buildHPrism(root: HTMLElement, g: Geo): void {
  const flash = span("pv-hp-flash");
  flash.style.left = `${Math.round(g.mx)}px`;
  flash.style.top = `${Math.round(g.my)}px`;
  root.appendChild(flash);
  if (g.hasAvatar) {
    const dx = g.ax - g.mx;
    const dy = g.ay - g.my;
    const dist = Math.max(20, Math.hypot(dx, dy) - g.ar * 0.2);
    const fan = span("pv-hp-fan");
    const h = Math.round(g.ar * 2.1);
    fan.style.left = `${Math.round(g.mx)}px`;
    fan.style.top = `${Math.round(g.my - h / 2)}px`;
    fan.style.width = `${Math.round(dist)}px`;
    fan.style.height = `${h}px`;
    fan.style.setProperty("--ang", `${Math.round((Math.atan2(dy, dx) * 180) / Math.PI)}deg`);
    root.appendChild(fan);
    root.appendChild(halo(g));
  }
  const mk = glowMark(g);
  mk.classList.add("pv-hp-mark");
  root.appendChild(mk);
}

/** ◇ 干渉縞: ✓ から虹の輪が 2 重に広がり、書いた人に届いて光る。 */
function buildHFringe(root: HTMLElement, g: Geo): void {
  const reach = g.hasAvatar ? Math.hypot(g.ax - g.mx, g.ay - g.my) : 80;
  // 輪はヘッダーの帯 (カードの上端 〜 ヘッダーの下端) の中だけで広げる。本文やボタンの上まで
  // 横切らせると、毎回見るには騒がしい。
  const clipH = g.by + g.hh + 2;
  const clip = span("pv-hr-clip");
  clip.style.height = `${Math.round(clipH)}px`;
  root.appendChild(clip);
  for (let i = 0; i < 2; i++) {
    const w = span("pv-hr-wave");
    const d = Math.round(reach * 2 + 8);
    w.style.left = `${Math.round(g.mx - d / 2)}px`;
    w.style.top = `${Math.round(g.my - d / 2)}px`;
    w.style.width = `${d}px`;
    w.style.height = `${d}px`;
    clip.appendChild(w);
  }
  if (g.hasAvatar) root.appendChild(halo(g));
  const mk = glowMark(g);
  mk.classList.add("pv-hr-mark");
  root.appendChild(mk);
}

/** 殻の左上の角 (無ければ本文列の左上)。回折と転箔の出発点。 */
function cornerOf(g: Geo, shell: ShellRect | null): { x: number; y: number } {
  if (shell) return { x: shell.x + 3, y: shell.y + 3 };
  return { x: g.bx + 3, y: g.by + g.hh + 4 };
}

/** アバターの円周上で、点 (x, y) にいちばん近い所 (少し内側)。 */
function avatarRim(g: Geo, x: number, y: number): { x: number; y: number } {
  const dx = x - g.ax;
  const dy = y - g.ay;
  const d = Math.hypot(dx, dy) || 1;
  return { x: g.ax + (dx / d) * g.ar * 0.92, y: g.ay + (dy / d) * g.ar * 0.92 };
}

/**
 * ◇ 回折 (Codex 推し): 殻の角の虹の弧で光が曲がり、書いた人へ届く → アバターの縁に虹の弧 →
 * ✓ を 2 画で描き、額が左から開く。
 */
function buildHDiffract(root: HTMLElement, g: Geo, shell: ShellRect | null): void {
  const c = cornerOf(g, shell);
  const arc = span("pv-hd-arc");
  arc.style.left = `${Math.round(c.x - 3)}px`;
  arc.style.top = `${Math.round(c.y - 3)}px`;
  root.appendChild(arc);
  if (g.hasAvatar) {
    const to = avatarRim(g, c.x, c.y);
    const path = span("pv-hd-path");
    path.style.left = `${Math.round(c.x)}px`;
    path.style.top = `${Math.round(c.y)}px`;
    path.style.setProperty("--dx", `${Math.round(to.x - c.x)}px`);
    path.style.setProperty("--dy", `${Math.round(to.y - c.y)}px`);
    const comet = span("pv-t-comet");
    comet.style.setProperty("--ang", `${Math.round((Math.atan2(to.y - c.y, to.x - c.x) * 180) / Math.PI)}deg`);
    comet.appendChild(span("pv-hd-dot"));
    path.appendChild(comet);
    root.appendChild(path);
    const rim = span("pv-hd-rim");
    const d = Math.round(g.ar * 2 + 8);
    rim.style.left = `${Math.round(g.ax - d / 2)}px`;
    rim.style.top = `${Math.round(g.ay - d / 2)}px`;
    rim.style.width = `${d}px`;
    rim.style.height = `${d}px`;
    root.appendChild(rim);
  }
  const mk = glowMark(g);
  mk.classList.add("pv-hd-mark");
  root.appendChild(mk);
}

/** ◇ 転箔: 殻の角の箔が小さな欠片になって書いた人へ移り、「守」の刻印として貼り付く。 */
function buildHTransfer(root: HTMLElement, g: Geo, shell: ShellRect | null): void {
  const c = cornerOf(g, shell);
  if (g.hasAvatar) {
    const to = { x: g.ax + g.ar * 0.62, y: g.ay + g.ar * 0.62 };
    const path = span("pv-ht-path");
    path.style.left = `${Math.round(c.x + 6)}px`;
    path.style.top = `${Math.round(c.y + 2)}px`;
    path.style.setProperty("--dx", `${Math.round(to.x - c.x - 6)}px`);
    path.style.setProperty("--dy", `${Math.round(to.y - c.y - 2)}px`);
    const arc = span("pv-ht-arc");
    const chip = span("pv-ht-chip");
    chip.appendChild(span("pv-ht-face", "守"));
    arc.appendChild(chip);
    path.appendChild(arc);
    root.appendChild(path);
  }
  const mk = glowMark(g);
  mk.classList.add("pv-ht-mark");
  root.appendChild(mk);
}

// ---------------------------------------------------------------------------
// 再生
// ---------------------------------------------------------------------------

/**
 * カードが画面の近くにあるか (遠ければ演出せず完成の状態にする)。
 * ⚠ 判定は画面の高さの半分ぶん甘くする。差し替えた直後のカードは content-visibility:auto の
 *   せいで、まだ仮の高さ (contain-intrinsic-size の 280px) で並んでいることがあり、
 *   きっちり判定すると画面に見えているのに「外」と出て演出が抜ける (5 枚同時で実際に踏んだ)。
 */
function visible(card: HTMLElement): boolean {
  const r = card.getBoundingClientRect();
  const vh = window.innerHeight || document.documentElement.clientHeight;
  const slack = vh / 2;
  return r.bottom > -slack && r.top < vh + slack && r.width > 0;
}

/**
 * 測り終わったカードで演出を組み立てて始める (DOM に書くだけ。ここでは測らない)。
 * 同じカードの前の再生は cancel() で片付けてから始める。
 */
function start(card: HTMLElement, rc: FxReceipt, g: Geo, v: Exclude<Variant, "legacy">, delay: number, shell: ShellRect | null): void {
  const t = TIMING[v];
  const run: Run = { timers: [], root: null, mark: g.mark, avatar: null, main: null, time: null };
  runs.set(card, run);

  const root = span(`pv-fx pv-fx--${v}`);
  root.setAttribute("aria-hidden", "true");
  root.style.cssText = `visibility:hidden;--pv-d:${delay}ms;--pv-ld:${t.label}ms` + (t.hit !== undefined ? `;--pv-hit:${t.hit}ms` : "");
  root.dataset.kind = rc.kind;
  run.root = root;

  const lab = label(rc, g, !!t.timeSlot);
  root.style.setProperty("--lw", `${lab.dataset.w || 0}px`);
  root.classList.add(lab.dataset.lp === "up" ? "pv-lp-up" : "pv-lp-right");

  switch (v) {
    case "transmit":
      buildTransmit(root, g);
      break;
    case "mint":
      buildMint(root, g);
      break;
    case "sign":
      buildSign(root, g);
      break;
    case "develop":
      buildDevelop(root, g, card, delay, run);
      break;
    case "circuit":
      buildCircuit(root, g, shell);
      break;
    case "unified":
      buildUnified(root, g, card, shell, delay, run);
      break;
    case "hcircuit":
      buildHCircuit(root, g, card, shell, delay, run);
      break;
    case "hfoil":
      buildHFoil(root, g);
      break;
    case "hprism":
      buildHPrism(root, g);
      break;
    case "hfringe":
      buildHFringe(root, g);
      break;
    case "hdiffract":
      buildHDiffract(root, g, shell);
      break;
    case "htransfer":
      buildHTransfer(root, g, shell);
      break;
  }
  root.appendChild(lab);
  card.appendChild(root);
  clampLabel(lab, card);
  if (lab.dataset.lp === "time" && g.time) timeSwap(card, root, lab, g, run, delay + t.label, t.labelMs ?? LABEL_MS);

  // 本物の ✓ は演出が着地するまで伏せる (visibility なので幅は変わらない)。
  const mark = g.mark;
  if (mark) {
    mark.classList.remove("pv-fx-land");
    mark.classList.add("pv-fx-hold");
    later(run, () => {
      mark.classList.remove("pv-fx-hold");
      mark.classList.add("pv-fx-land");
    }, delay + (reduced ? 0 : t.land));
    later(run, () => mark.classList.remove("pv-fx-land"), delay + t.land + 700);
  }
  // アバターが受け取った瞬間に小さく弾む (送達・回路)。transform だけなので行は動かない。
  const av = card.querySelector<HTMLElement>(".post-avatar-col");
  if (av && t.hit !== undefined && g.hasAvatar && !reduced) {
    run.avatar = av;
    later(run, () => av.classList.add("pv-fx-recv"), delay + t.hit);
    later(run, () => av.classList.remove("pv-fx-recv"), delay + t.hit + 600);
  }
  later(run, () => cancel(card), delay + t.label + (t.labelMs ?? LABEL_MS) + 60);
}

/**
 * 「上」に置いた文言がカードの左右・上からはみ出さないよう寄せる (長い「書いた人が払いました」
 * が右端の ✓ の上に来たとき)。実際の幅は置いてからでないと分からないので、ここで 1 回だけ測る。
 */
function clampLabel(lab: HTMLElement, card: HTMLElement): void {
  if (lab.dataset.lp !== "up") return;
  const w = lab.offsetWidth;
  const h = lab.offsetHeight;
  const cw = card.clientWidth;
  const x = parseFloat(lab.style.left) || 0;
  const y = parseFloat(lab.style.top) || 0;
  lab.style.left = `${Math.round(Math.min(Math.max(x, 8 + w / 2), cw - 8 - w / 2))}px`;
  lab.style.top = `${Math.round(Math.max(y, 4 + h / 2))}px`;
}

/** カードの見た目の地色 (透明なら親をたどる)。額が時刻より長いときの目隠しに使う。 */
function surfaceColor(card: HTMLElement): string {
  for (let e: HTMLElement | null = card; e; e = e.parentElement) {
    const c = window.getComputedStyle(e).backgroundColor;
    if (c && c !== "transparent" && !/rgba\([^)]*,\s*0\)$/.test(c)) return c;
  }
  return "var(--bg-primary)";
}

/**
 * 額を時刻の場所で入れ替える (◇ 干渉縞): 時刻が上へ抜け、同じ場所へ下から額が入り、
 * 最後は額が上へ抜けて時刻が戻る。行の幅は変わらない (時刻は visibility ではなく
 * transform と filter で動かすだけ)。
 * 額が時刻より長い (「書いた人が払いました」など) ときは、はみ出す左側を地色でなめらかに覆う。
 */
function timeSwap(card: HTMLElement, root: HTMLElement, lab: HTMLElement, g: Geo, run: Run, at: number, dur: number): void {
  const tm = g.time;
  if (!tm) return;
  const slot = tm.r - tm.l;
  const w = lab.offsetWidth;
  if (w > slot + 1) {
    const room = tm.r - g.metaL - 2;
    if (w > room) lab.classList.add("pv-lp-small");
    const cover = Math.min(Math.max(lab.offsetWidth, slot) + 16, Math.max(slot, room + 16));
    const chip = span("pv-fx-chip");
    chip.style.right = `${Math.round(g.cw - tm.r - 2)}px`;
    chip.style.top = `${Math.round(tm.cy)}px`;
    chip.style.width = `${Math.round(cover)}px`;
    chip.style.height = `${Math.round(tm.h + 6)}px`;
    chip.style.setProperty("--pv-chip-bg", surfaceColor(card));
    root.insertBefore(chip, lab);
  }
  run.time = tm.el;
  tm.el.style.setProperty("--pv-tf-d", `${at}ms`);
  tm.el.style.setProperty("--pv-tf-dur", `${dur}ms`);
  tm.el.classList.add("pv-fx-timeflip");
}

/**
 * カード 1 枚の演出を再生する。variant を省くとデモで選ばれている案。
 * legacy は false を返す (呼び出し側が旧来の「−N 守」の印を出す)。
 */
export function play(
  card: HTMLElement,
  rc: FxReceipt,
  opts: { delay?: number; shell?: ShellRect | null; variant?: Variant } = {},
): boolean {
  cancel(card); // legacy を選んでいても、前の演出は必ず片付ける
  const v = opts.variant || currentVariant();
  if (v === "legacy") return false;
  reduced = reducedMotion();
  if (!visible(card)) return true;
  const g = measure(card);
  if (!g) return true;
  start(card, rc, g, v, Math.max(0, Math.round(opts.delay || 0)), opts.shell || null);
  return true;
}

/**
 * 同時に解錠されたカードを、画面の上から順に少しずつずらして再生する。
 * 先に全部を測ってから書く (測る → 書く → 測る… と交互にするとカードの数だけレイアウトが走る)。
 * 戻り値は演出を出さなかったもの (デモで「いまの印」を選んでいるとき) — 呼び出し側が旧来の印を出す。
 */
export function playBatch<T extends { card: HTMLElement; rc: FxReceipt; shell?: ShellRect | null }>(items: T[]): T[] {
  const v = currentVariant();
  const live = items.filter((it) => it.card.isConnected);
  live.forEach((it) => cancel(it.card));
  if (v === "legacy") return live;
  reduced = reducedMotion();
  const measured = live
    .map((it) => ({ it, top: it.card.getBoundingClientRect().top, on: visible(it.card), g: measure(it.card) }))
    .filter((m) => m.on && m.g)
    .sort((a, b) => a.top - b.top);
  measured.forEach((m, i) => {
    start(m.it.card, m.it.rc, m.g as Geo, v, Math.min(i, STAGGER_CAP) * STAGGER_MS, m.it.shell || null);
  });
  return [];
}

/** 殻の識別線の位置 (差し替える直前に呼ぶ)。 */
export function shellRectOf(card: HTMLElement): ShellRect | null {
  const box = card.querySelector<HTMLElement>(".post-locked");
  if (!box) return null;
  const base = card.getBoundingClientRect();
  const r = box.getBoundingClientRect();
  if (r.height === 0) return null;
  return { x: r.left - base.left, y: r.top - base.top, h: r.height, w: r.width };
}

// ---------------------------------------------------------------------------
// 払っている間 (解錠の応答を待つ間) の表示 — 2026-09-19 デモ
// ---------------------------------------------------------------------------
//
// オーナー指示「この状態の表示も、あらゆるデザイン案を作って」。Codex (gpt-6-astra) の 10 案
// (蓄光 / 光包 / 走査 / 開環 / 薄貨 / 信号 / 送路 / 巡縁 / 静標 / 言葉) に、Claude の 2 案
// (電界 / 集光) を足した 12 案 + いまの表示。どれも:
//   - **長さが読めない** (ふつう 100〜300ms、遅い回線で数秒)。静止の姿を 0ms で出し、動きは
//     140ms 後から (速いときは動く前に本文へ替わる = 一瞬だけ何かが光る不自然さが無い)。
//   - **量・割合・残り時間を表さない** (満ちていく棒にしない)。ループは「生きている」印だけ。
//   - **殻の箱の高さを変えない**。飾りは箱の中に absolute で重ねる (開環・薄貨だけは輪の場所)。
//   - 同時に何枚も待つときは、ループの始まりを画面の上から最大 180ms ずらす (--pv-pd-d)。
//   - 成功すると、左端の識別線から ◎ 統合の回路が走り出す (蓄光・静標・送路・光包・集光・信号は
//     「識別線の下端」に光を集めておくので、そのまま放電に見える)。

export const PAY_VARIANTS = [
  "charge", "packet", "scan", "ring", "coin", "signal", "route", "orbit", "node", "words", "aurora", "gather", "legacy",
  // ホログラムのセット用 (火花が縁をめぐる / 箔を傾けた光 / 虹の扇 / 干渉縞)
  "hspark", "htilt", "hfan", "hfilm",
  // Codex のセット用 (角の虹の弧 / 角の箔の照り)
  "hcorner", "hglint",
] as const;
export type PayVariant = (typeof PAY_VARIANTS)[number];
const PAY_KEY = "mp_pv_pay_2";
const DEFAULT_PAY: PayVariant = "hfilm"; // 干渉縞のセット

/** 「N 守 ◯◯」の文言 (デモで切り替える)。 */
export const PAY_TEXTS = {
  pay: "払っています…",
  send: "届けています…",
  ready: "届ける準備中",
  writer: "書いた人へ…",
} as const;
export type PayText = keyof typeof PAY_TEXTS;
const PAY_TEXT_KEY = "mp_pv_paytext";
const DEFAULT_PAY_TEXT: PayText = "send";

function readLS<T extends string>(key: string, ok: readonly string[], def: T): T {
  try {
    const v = window.localStorage.getItem(key);
    if (v && ok.includes(v)) return v as T;
  } catch {
    /* 読めなければ既定 */
  }
  return def;
}
function writeLS(key: string, v: string): void {
  try {
    window.localStorage.setItem(key, v);
  } catch {
    /* 保存できなくてもよい */
  }
}
export function currentPay(): PayVariant {
  return readLS(PAY_KEY, PAY_VARIANTS, DEFAULT_PAY);
}
export function setPay(v: PayVariant): void {
  writeLS(PAY_KEY, v);
}
export function currentPayText(): PayText {
  return readLS(PAY_TEXT_KEY, Object.keys(PAY_TEXTS), DEFAULT_PAY_TEXT);
}
export function setPayText(v: PayText): void {
  writeLS(PAY_TEXT_KEY, v);
}

function el(tag: string, cls: string, text?: string): HTMLElement {
  const e = document.createElement(tag);
  e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
}

/** 案ごとの飾り (箱の中に重ねるもの / 1 行目の輪の場所に置くもの)。 */
function payDecor(v: PayVariant): { layer: HTMLElement | null; inline: HTMLElement | null } {
  const layer = el("span", `pv-pd pv-pd--${v}`);
  layer.setAttribute("aria-hidden", "true");
  let inline: HTMLElement | null = null;
  switch (v) {
    case "charge":
      layer.appendChild(el("i", "pv-pd-core"));
      break;
    case "packet":
      layer.appendChild(el("i", "pv-pd-lane"));
      for (let i = 0; i < 2; i++) layer.appendChild(el("i", "pv-pd-pk"));
      layer.appendChild(el("i", "pv-pd-core"));
      break;
    case "scan":
      layer.appendChild(el("i", "pv-pd-band"));
      break;
    case "ring":
      inline = el("i", "pv-pd-inline pv-pd-ring");
      break;
    case "coin":
      inline = el("i", "pv-pd-inline pv-pd-coin");
      inline.appendChild(el("b", "", "守"));
      break;
    case "signal":
      for (let i = 0; i < 5; i++) layer.appendChild(el("i", "pv-pd-bar"));
      break;
    case "route":
      layer.appendChild(el("i", "pv-pd-path"));
      layer.appendChild(el("i", "pv-pd-comet"));
      layer.appendChild(el("i", "pv-pd-core"));
      break;
    case "orbit":
      layer.appendChild(el("i", "pv-pd-orbit"));
      break;
    case "node":
      layer.appendChild(el("i", "pv-pd-node"));
      break;
    case "aurora":
      layer.appendChild(el("i", "pv-pd-aurora"));
      layer.appendChild(el("i", "pv-pd-core"));
      break;
    case "gather":
      for (let i = 0; i < 3; i++) layer.appendChild(el("i", "pv-pd-dot"));
      layer.appendChild(el("i", "pv-pd-core"));
      break;
    case "hspark":
      layer.appendChild(el("i", "pv-pd-orbit pv-pd-hspark"));
      break;
    case "htilt":
      layer.appendChild(el("i", "pv-pd-htilt"));
      break;
    case "hfan":
      layer.appendChild(el("i", "pv-pd-hfan"));
      break;
    case "hfilm":
      layer.appendChild(el("i", "pv-pd-hfilm"));
      break;
    case "hcorner":
      layer.appendChild(el("i", "pv-pd-hcorner"));
      break;
    case "hglint":
      layer.appendChild(el("i", "pv-pd-hglint"));
      break;
    case "words":
    case "legacy":
      return { layer: null, inline: null };
  }
  if (inline) inline.setAttribute("aria-hidden", "true");
  return { layer: layer.childElementCount ? layer : null, inline };
}

/**
 * 払っている間の見た目を付け外しする (paidview.ts の setUnlocking から)。
 * 文言の hidden の出し分けは setUnlocking の持ち物 — ここは飾りと「N 守 ◯◯」の語だけ。
 * @returns legacy (いまの表示) のときは false (呼び出し側がいままでの輪を出す)。
 */
export function showPaying(card: HTMLElement, on: boolean): boolean {
  // 位置の読み取りは書き込みより先に (読み → 書き → 読み… と交互にしない)。
  const top = on ? card.getBoundingClientRect().top : 0;
  for (const c of Array.from(card.classList)) if (c.startsWith("pv-pay")) card.classList.remove(c);
  card.style.removeProperty("--pv-pd-d");
  const box = card.querySelector<HTMLElement>(".post-locked");
  if (!box) return true;
  box.querySelectorAll(":scope .pv-pd, :scope .pv-pd-inline").forEach((n) => n.remove());
  const paying = box.querySelector<HTMLElement>(".post-locked-paying");
  let v = currentPay();
  // 価格不明の殻 (Price 0) には「N 守 ◯◯」の行が無い。言葉の案はそこで伝えられないので
  // いままでの輪に任せる (Codex レビュー: 何も出ず、待っているのか分からなくなっていた)。
  if (v === "words" && !paying) v = "legacy";
  if (!on) return v !== "legacy";
  card.classList.add("pv-pay", `pv-pay--${v}`);
  // ループの始まりを画面の上から最大 180ms ずらす (何枚も同時に待つとき、揃って明滅しない)。
  const vh = window.innerHeight || 800;
  card.style.setProperty("--pv-pd-d", `${Math.round(Math.min(1, Math.max(0, top / vh)) * 180)}ms`);

  // 文言: 「N 守」の後ろの語だけ替える (いまの表示は「払っています…」のまま)。
  if (paying) {
    const word = v === "legacy" ? PAY_TEXTS.pay : v === "words" ? "書いた人へ届けています…" : PAY_TEXTS[currentPayText()];
    let tail = paying.querySelector<HTMLElement>(":scope > .pv-pd-word");
    if (!tail) {
      // テンプレートの「 払っています…」の文字 (strong の後ろ) を 1 つの span に包み直す。
      Array.from(paying.childNodes).forEach((n) => {
        if (n.nodeType === Node.TEXT_NODE) n.remove();
      });
      tail = el("span", "pv-pd-word");
      paying.appendChild(tail);
    }
    tail.textContent = word;
  }
  if (v === "legacy") return false;
  const { layer, inline } = payDecor(v);
  // 古い CSS を握った端末で素のまま出ないよう、インラインで伏せて CSS の !important で開ける。
  if (layer) {
    layer.style.visibility = "hidden";
    box.appendChild(layer);
  }
  if (inline) {
    inline.style.visibility = "hidden";
    // 価格不明の殻には「N 守 ◯◯」の行が無いので、1 行目の頭に置く。
    const host = paying || box.querySelector<HTMLElement>(":scope > .post-locked-line") || box;
    host.insertBefore(inline, host.firstChild);
  }
  return true;
}

// ---------------------------------------------------------------------------
// 殻の枠そのもの (2026-09-19 デモ、オーナー「枠のデザイン自体もいろんな案を」)
// ---------------------------------------------------------------------------
// CSS だけで作る (v2.css §241)。<html data-pv-frame="…"> で着せ替えるので、比較ページの殻も
// タイムラインの殻も同時に替わる。ページを開いた瞬間に旧い枠が一瞬出ないよう、base.html の head
// でも同じ値を読んで属性を置いている (デモの間だけ。決まったら CSS の既定にして両方消す)。

export const FRAME_VARIANTS = [
  "classic", "glass", "frost", "ticket", "board", "holo", "line", "pill", "neon", "matrix", "cipher", "seal",
  // Codex (gpt-6-astra) の 5 案 (Claude の 11 案とかぶらない方向: 形・材質・奥行き)
  "carve", "fold", "weave", "stack", "ripple",
] as const;
export type FrameVariant = (typeof FRAME_VARIANTS)[number];
const FRAME_KEY = "mp_pv_frame_2";
const DEFAULT_FRAME: FrameVariant = "holo"; // 2026-09-19 オーナー決定「ホログラムでいきます」

export function currentFrame(): FrameVariant {
  return readLS(FRAME_KEY, FRAME_VARIANTS, DEFAULT_FRAME);
}
export function applyFrame(): void {
  document.documentElement.setAttribute("data-pv-frame", currentFrame());
}
export function setFrame(v: FrameVariant): void {
  writeLS(FRAME_KEY, v);
  applyFrame();
}

type LegacyStamp = (card: HTMLElement, rc: FxReceipt & { live: string }) => void;

declare global {
  interface Window {
    MpPaidFx?: {
      play: typeof play;
      playBatch: typeof playBatch;
      variants: readonly Variant[];
      current: typeof currentVariant;
      set: typeof setVariant;
      /** 旧来の「−N 守」(比較用)。 */
      legacy: LegacyStamp;
      /** 払っている間の表示 (paidview.ts の setUnlocking と同じもの)。 */
      paying: (card: HTMLElement, on: boolean) => void;
      payVariants: readonly PayVariant[];
      pay: typeof currentPay;
      setPay: typeof setPay;
      payText: typeof currentPayText;
      setPayText: typeof setPayText;
      frameVariants: readonly FrameVariant[];
      frame: typeof currentFrame;
      setFrame: typeof setFrame;
    };
  }
}

/** /testpage/paid-fx から呼べるように公開する (デモ用。決まったら外す)。 */
export function exposePaidFx(legacy: LegacyStamp, paying: (card: HTMLElement, on: boolean) => void): void {
  window.MpPaidFx = {
    play, playBatch, variants: VARIANTS, current: currentVariant, set: setVariant, legacy,
    paying, payVariants: PAY_VARIANTS, pay: currentPay, setPay, payText: currentPayText, setPayText,
    frameVariants: FRAME_VARIANTS, frame: currentFrame, setFrame,
  };
  applyFrame();
}
